import { useEffect, useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

import { getFile } from 'src/utils/file';
import { Button } from '@/components/ui/button';
import { cn } from 'src/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

type Props = {
  fileName: string;
  termsSignedDownloadPdfUrl?: string;
  terms: string;
  className?: string;
};

const AgreementSkeleton = () => (
  <div className="flex flex-col gap-6 h-full">
    <div className="text-center space-y-4">
      <Skeleton className="h-8 w-3/4 mx-auto" />
      <Skeleton className="h-6 w-1/2 mx-auto" />
      <Skeleton className="h-4 w-1/3 mx-auto" />
    </div>

    <div className="space-y-4">
      <Skeleton className="h-4 w-1/4" />
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-11/12" />
        <Skeleton className="h-4 w-10/12" />
      </div>
    </div>
    <div className="space-y-2">
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-4 w-1/4" />
    </div>
    <div className="space-y-6">
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/4 font-semibold" />
        <div className="space-y-2 pl-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-9/12" />
        </div>
      </div>
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/3 font-semibold" />
        <div className="space-y-2 pl-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-10/12" />
        </div>
      </div>
    </div>
    <div className="mt-8 space-y-6">
      <Skeleton className="h-6 w-1/4" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-16 w-32 rounded" />
          <Skeleton className="h-4 w-1/3" />
        </div>
        <div className="space-y-3">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-16 w-32 rounded" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </div>
    </div>
    <div className="mt-auto pt-6 space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  </div>
);

const GeneratedAgreementContent = ({ fileName, terms, termsSignedDownloadPdfUrl, className }: Props) => {
  const [signedPdf, setSignedPdf] = useState('');
  const [isLoading, setIsLoading] = useState(!!termsSignedDownloadPdfUrl);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!termsSignedDownloadPdfUrl) return;

    const getSignedPdfUrl = async () => {
      setIsLoading(true);

      try {
        const responseFile = await getFile(termsSignedDownloadPdfUrl);

        if (!responseFile.ok) {
          setSignedPdf('');
          return;
        }

        const blob = await responseFile.blob();

        setSignedPdf(URL.createObjectURL(blob));
      } catch {
        toast.error('Failed to load pdf');
      } finally {
        setIsLoading(false);
      }
    };

    getSignedPdfUrl();
  }, []);

  const onZoomIn = () => setScale((prevScale) => prevScale + 0.1);
  const onZoomOut = () => {
    setScale((prevScale) => prevScale - 0.1);
  };

  return (
    <div className={cn('grow flex flex-col min-w-0 h-full', className)}>
      <div className="flex items-center justify-between gap-3 py-2 px-3 h-9 bg-[#86A7B9]">
        <div className="min-w-0 grow">
          <h6 className="text-white text-sm font-semibold truncate">{fileName}</h6>
        </div>
        <div className="flex shrink-0 space-x-4">
          <Button
            className="bg-[#6A8493] size-5 p-0 rounded-full"
            disabled={scale >= 1.8 || isLoading}
            onClick={onZoomIn}
            data-testid="zoom-in-button"
          >
            <Plus className="size-2.5!" />
          </Button>
          <Button
            className="bg-[#6A8493] size-5 p-0 rounded-full"
            disabled={scale < 0.8 || isLoading}
            onClick={onZoomOut}
            data-testid="zoom-out-button"
          >
            <Minus className="size-2.5!" />
          </Button>
        </div>
      </div>
      <div className="p-4 bg-[#D9E5EC] overflow-auto grow">
        <div
          className={cn(
            'bg-white shadow-sm px-4 md:px-[40px] py-5 md:py-[50px]',
            scale < 1 && 'origin-[top_center]',
            scale >= 1 && 'origin-top-left',
            signedPdf && 'h-full',
          )}
          style={{ transform: `scale(${scale})` }}
          data-testid="pdf-container"
        >
          {isLoading ? (
            <AgreementSkeleton />
          ) : signedPdf ? (
            <object
              data={`${signedPdf}#view=fitH&toolbar=0&navpanes=0&scrollbar=0`}
              type="application/pdf"
              width="100%"
              height="100%"
              title="Agreement"
              data-testid="pdf-object"
            >
              <p>Failed to load pdf</p>
            </object>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: terms,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratedAgreementContent;
